import type { ComponentType } from 'react';
import { lazy, LazyExoticComponent } from 'react';

const CHUNK_RETRY_KEY = 'mirray-chunk-reload';

function isLikelyChunkLoadFailure(error: unknown): boolean {
  if (error == null) return false;
  const name = (error as { name?: string }).name;
  if (name === 'ChunkLoadError') return true;
  const msg = String((error as { message?: unknown }).message ?? error);
  return /chunk|dynamically imported module|importing a module script failed|failed to fetch|loading chunk|css.*chunk/i.test(
    msg,
  );
}

/**
 * Wraps dynamic imports for React.lazy. After a new deploy, users may still have an old
 * HTML shell that points at removed hashed chunks; a single full reload fetches a fresh
 * index and asset manifest. See ChunkLoadError / "Importing a module script failed" in Safari.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React.lazy’s loose component typing
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() =>
    importFn()
      .then((m) => {
        try {
          sessionStorage.removeItem(CHUNK_RETRY_KEY);
        } catch {
          /* ignore */
        }
        return m;
      })
      .catch((error) => {
        if (isLikelyChunkLoadFailure(error)) {
          try {
            if (!sessionStorage.getItem(CHUNK_RETRY_KEY)) {
              sessionStorage.setItem(CHUNK_RETRY_KEY, '1');
              window.location.reload();
              return new Promise<{ default: T }>(() => {});
            }
          } catch {
            /* sessionStorage may be unavailable; fall through */
          }
        }
        throw error;
      }),
  );
}
